import { FC, useCallback } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { SpaListTemplate } from 'voyager';

import { useGetSpaFacilitiesPage } from '@features/spa/hooks/useGetSpaFacilitiesPage';
import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { FacilitiesV2Props } from './FacilitiesV2.types';
import { useFacilitiesList } from './hooks/useFacilitiesList';

export const FacilitiesV2: FC<FacilitiesV2Props> = ({
  property,
  journeyParams,
  facilitiesCategories = [],
  categoryId = null,
  userHasAccessToFacilities = false,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.FACILITY_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    guests: journeyParams?.spaAdults,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const { listItems, uncategorizedItems, isError, isInitialLoading, isRefetching } =
    useFacilitiesList({
      propertyId: property.id,
      language,
      categoryFilterId: filter.categoryFilterId,
      categoryFilterValue: filter.categoryFilterValue,
      date: filter.appliedDateString,
      guests: filter.guests,
    });

  const { data: facilitiesPage } = useGetSpaFacilitiesPage(property.id, language);

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  if (!userHasAccessToFacilities) {
    return (
      <SpaEntityError
        errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
        values={{ spaEntity: 'facilities' }}
      />
    );
  }

  const categoryOptions = buildCategoryOptions(
    facilitiesCategories,
    t('spa.package.allCategories'),
    uncategorizedItems.length > 0 ? t('spa.noCategory') : undefined
  );

  const handleSelectFacility = (id: string) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
  };

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="spa"
            showGuests
            categories={navCategories}
            category="facilities"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <Spinner showDelayMessage isActive={isRefetching} />
        <SpaListTemplate
          items={listItems}
          onSelectItem={handleSelectFacility}
          isLoading={isInitialLoading}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          titleLabel={facilitiesPage?.title || t('app.facilities')}
          categoryLabel={t('common.category')}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('common.unavailableForSearch')}
          editSearchLabel={t('common.editSearch')}
          onEditSearch={handleEditSearch}
          noResultsLabel={isError ? t('spa.integrationUnavailable') : t('app.noItemsFound')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(min, max) =>
            min === max
              ? t('common.numberOfGuests', { guestsAmount: min })
              : t('common.guestRange', { from: min, to: max })
          }
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
